import { useCtx } from '@components/layout/leftNav/menus/units/CourseUnitsMenu';
import { CourseMetaDataQuery, CourseTerm } from '@graphql/generated-graphql';
import { useCourseLevelPermissions, useCourseMetaData } from '@hooks/courses/useCourseActivities';
import { useCourseTermsHandler, useCourseTermsStore } from '@hooks/courses/useCourseTerms';
import { useQueryParams } from '@hooks/useQueryParams';
import { useAutoSwitchEnrollment } from '@hooks/user/useImpersonateUser';
import React, { useContext, useEffect, useRef } from 'react';
import { UseQueryResult } from 'react-query';

export type CourseContextType = UseQueryResult<
    NonNullable<CourseMetaDataQuery['courses']>[0] | undefined,
    unknown
>;

type CoursePermissionsTypes = ReturnType<typeof useCourseLevelPermissions>;

type CourseTermType = ReturnType<typeof useCourseTermsHandler>;

const CourseTermContext = React.createContext<CourseTermType | null>(null);
const CourseContext = React.createContext<CourseContextType | null>(null);
const CoursePermissionContext = React.createContext<CoursePermissionsTypes | null>(null);

export function CourseContextProvider({ children }: { children: React.ReactNode }) {
    const course = useCourseMetaData();
    const { role } = useQueryParams(['role']);
    const { isLoading: mutationsLoading, mutate } = useAutoSwitchEnrollment();

    const state = useCourseTermsHandler();
    const coursePermissions = useCourseLevelPermissions(course);

    useEffect(() => {
        if (
            !!role &&
            !coursePermissions?.isLoading &&
            coursePermissions?.permissions?.currentRole?.role?.type !== role &&
            coursePermissions?.permissions?.otherEnrollments?.length > 0 &&
            coursePermissions?.permissions?.otherEnrollments?.find((e) => e.role?.type === role)?.id
        ) {
            mutate({
                id: coursePermissions?.permissions?.otherEnrollments?.find(
                    (e) => e.role?.type === role,
                )?.id,
            });
        }
    }, [role, coursePermissions?.permissions?.otherEnrollments, coursePermissions?.isLoading]);

    useEffect(() => {
        if (!!course?.data) {
            useCourseTermsStore
                .getState()
                .initializeTerm(
                    course?.data?.id,
                    course?.data?.currentTerm as CourseTerm,
                    course?.data?.terms as CourseTerm[],
                );
        }
    }, [course?.data?.id, course?.data?.currentTerm, course?.data?.terms]);

    return (
        <CourseTermContext.Provider value={state}>
            <CourseContext.Provider value={course}>
                <CoursePermissionContext.Provider value={coursePermissions}>
                    {children}
                </CoursePermissionContext.Provider>
            </CourseContext.Provider>
        </CourseTermContext.Provider>
    );
}

export function useCourseStateContext() {
    const context = useContext(CourseContext);
    if (context === null) {
        throw new Error('Expected Context for <CourseContext /> to be Initialized');
    }

    return context;
}

export function useCoursePermissions() {
    const context = useContext(CoursePermissionContext);
    if (context === null) {
        throw new Error(
            'Expected Dispatch Context for <CourseDispatchContext /> to be Initialized',
        );
    }

    return context;
}

export function useCourseTermContext() {
    const c = useCtx(CourseTermContext);
    return c;
}
